import type {
  GeoBrowseDegRow,
  GeoBrowseDisease,
  GeoBrowseEnrichmentRow,
  GeoBrowseGene,
  GeoBrowsePcaPoint,
  GeoBrowseSample,
  GeoBrowseStudy,
  GeoBrowseStudyDetail,
  GeoBrowseVolcanoPoint,
} from '../types';

export type ApiDisease = {
  cui: string;
  cuiName: string;
  nstudiesTotal: number;
  nstudiesUsable: number;
  nstudiesWithDeg: number;
  nsamplesTotal: number;
  ngenesTested?: number;
  ngenesSig?: number;
};

export type ApiStudyDetail = {
  cui: string;
  cuiName?: string;
  accession: string;
  comparisonId?: string;
  title: string;
  summary?: string | null;
  overallDesign?: string | null;
  year: number;
  pubmedids?: string | null;
  platform?: string | null;
  usableForDeg: boolean;
  controlCaveat?: string | null;
  qcFlagRate?: number | null;
  hasDeg: boolean;
  hasGseaCurated?: boolean;
  hasGseaAll?: boolean;
  hasGseaReactome?: boolean;
  hasGseaWikipathways?: boolean;
  nsamples: number;
  ncase: number;
  ncontrol: number;
  ngenesTested?: number;
  ngenesSig?: number;
  qcFlags?: number;
  covariatesInModel?: string | null;
  confidentSexMismatches?: number;
  confidentGroupConsistencyFlags?: number;
};

export type ApiSample = {
  cui?: string;
  accession?: string;
  sampleId: string;
  group?: string | null;
  sex?: string | null;
  sexSource?: string | null;
  tissue?: string | null;
};

export type ApiPcaPoint = {
  cui?: string;
  accession?: string;
  sampleId: string;
  group?: string | null;
  pc1: number;
  pc2: number;
  pctVarPc1: number;
  pctVarPc2: number;
  ngenesUsed?: number;
};

export type ApiDegRow = {
  cui?: string;
  accession?: string;
  comparisonId?: string;
  geneId: string | number;
  geneSymbol: string;
  baseMean: number;
  log2FoldChange: number;
  lfcSE: number;
  stat: number;
  pvalue: number;
  padj: number;
};

export type ApiVolcanoPoint = {
  cui?: string;
  accession?: string;
  comparisonId?: string;
  geneId: string | number;
  geneSymbol: string;
  log2FoldChange: number;
  padj: number;
  significant?: boolean;
};

export type ApiEnrichmentRow = {
  cui?: string;
  accession?: string;
  comparisonId?: string;
  collection: string;
  geneSetId: string;
  geneSetName: string;
  es: number;
  nes: number;
  nomPval: number;
  fdrQval: number;
  fwerPval?: number;
  tagPct: number;
  genePct: number;
  leadGenes: string;
};

function num(value: unknown, fallback = 0): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function str(value: unknown, fallback = ''): string {
  if (value == null) return fallback;
  return String(value);
}

function pick(row: Record<string, unknown>, keys: string[]): unknown {
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null && row[key] !== '') {
      return row[key];
    }
  }
  return undefined;
}

function pickNum(row: Record<string, unknown>, keys: string[], fallback = 0): number {
  return num(pick(row, keys), fallback);
}

function pickStr(row: Record<string, unknown>, keys: string[], fallback = ''): string {
  const value = pick(row, keys);
  return value == null ? fallback : str(value, fallback);
}

export function unwrapApiList<T>(payload: unknown): { rows: T[]; total?: number } {
  if (Array.isArray(payload)) return { rows: payload as T[] };
  if (!payload || typeof payload !== 'object') return { rows: [] };

  const page = payload as Record<string, unknown>;
  const rows = [page.content, page.items, page.data, page.results, page.genes].find(Array.isArray) as
    | T[]
    | undefined;
  const totalRaw = page.totalElements ?? page.total ?? page.count;
  const total = typeof totalRaw === 'number' ? totalRaw : undefined;
  return { rows: rows ?? [], total };
}

export function mapApiDisease(row: ApiDisease): GeoBrowseDisease {
  const r = row as ApiDisease & Record<string, unknown>;
  return {
    cui: str(r.cui),
    cui_name: pickStr(r, ['cuiName', 'cui_name']),
    n_studies_total: pickNum(r, ['nstudiesTotal', 'nStudiesTotal', 'n_studies_total']),
    n_studies_usable: pickNum(r, ['nstudiesUsable', 'nStudiesUsable', 'n_studies_usable']),
    n_studies_with_deg: pickNum(r, ['nstudiesWithDeg', 'nStudiesWithDeg', 'n_studies_with_deg']),
    n_samples_total: pickNum(r, ['nsamplesTotal', 'nSamplesTotal', 'n_samples_total']),
    n_genes_tested: pickNum(r, ['ngenesTested', 'nGenesTested', 'n_genes_tested']),
    n_genes_sig: pickNum(r, ['ngenesSig', 'nGenesSig', 'n_genes_sig']),
  };
}

const GENE_MAPPED_KEYS = new Set([
  'geneId',
  'gene_id',
  'geneSymbol',
  'gene_symbol',
  'symbol',
  'typeOfGene',
  'type_of_gene',
  'nDiseases',
  'ndiseases',
  'n_diseases',
  'nDiseasesSig',
  'ndiseasesSig',
  'n_diseases_sig',
  'nStudies',
  'nstudies',
  'n_studies',
  'nStudiesSig',
  'nstudiesSig',
  'n_studies_sig',
  'nDegRows',
  'ndegRows',
  'n_deg_rows',
  'nSigRows',
  'nsigRows',
  'n_sig_rows',
]);

export type ApiGene = Record<string, unknown> & {
  geneId?: string | number;
  geneSymbol?: string;
  symbol?: string;
  typeOfGene?: string | null;
  nDiseases?: number;
  ndiseases?: number;
  nDiseasesSig?: number;
  ndiseasesSig?: number;
  nStudies?: number;
  nstudies?: number;
  nStudiesSig?: number;
  nstudiesSig?: number;
  nDegRows?: number;
  ndegRows?: number;
  nSigRows?: number;
  nsigRows?: number;
};

export function mapApiGene(row: ApiGene): GeoBrowseGene {
  const extras: Record<string, string | number | boolean> = {};
  Object.entries(row).forEach(([key, value]) => {
    if (GENE_MAPPED_KEYS.has(key)) return;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      extras[key] = value;
    }
  });

  return {
    gene_id: pickNum(row, ['geneId', 'gene_id']),
    gene_symbol: pickStr(row, ['geneSymbol', 'gene_symbol', 'symbol']),
    type_of_gene: pickStr(row, ['typeOfGene', 'type_of_gene']),
    n_diseases: pickNum(row, ['nDiseases', 'ndiseases', 'n_diseases']),
    n_diseases_sig: pickNum(row, ['nDiseasesSig', 'ndiseasesSig', 'n_diseases_sig']),
    n_studies: pickNum(row, ['nStudies', 'nstudies', 'n_studies']),
    n_studies_sig: pickNum(row, ['nStudiesSig', 'nstudiesSig', 'n_studies_sig']),
    n_deg_rows: pickNum(row, ['nDegRows', 'ndegRows', 'n_deg_rows']),
    n_sig_rows: pickNum(row, ['nSigRows', 'nsigRows', 'n_sig_rows']),
    extras: Object.keys(extras).length ? extras : undefined,
  };
}

export type ApiStudy = {
  cui: string;
  cuiName?: string;
  accession: string;
  comparisonId?: string;
  title?: string;
  overallDesign?: string;
  year?: number;
  platform?: string;
  nsamples?: number;
  ncase?: number;
  ncontrol?: number;
  usableForDeg?: boolean;
  hasDeg?: boolean;
  ngenesTested?: number;
  ngenesSig?: number;
  qcFlags?: number;
};

export function mapApiStudy(row: ApiStudy): GeoBrowseStudy {
  return {
    cui: row.cui,
    cui_name: str(row.cuiName),
    accession: row.accession,
    comparison_id: str(row.comparisonId, `${row.cui}__${row.accession}`),
    title: str(row.title),
    overall_design: str(row.overallDesign),
    year: num(row.year),
    platform: str(row.platform),
    n_samples: num(row.nsamples),
    n_case: num(row.ncase),
    n_control: num(row.ncontrol),
    usable_for_deg: row.usableForDeg ?? false,
    has_deg: row.hasDeg ?? false,
    n_genes_tested: num(row.ngenesTested),
    n_genes_sig: num(row.ngenesSig),
    qc_flags: num(row.qcFlags),
  };
}

export function mapApiStudyDetail(row: ApiStudyDetail): GeoBrowseStudyDetail {
  const qcFlagRate = num(row.qcFlagRate);
  const qcFlags = row.qcFlags != null ? num(row.qcFlags) : Math.round(qcFlagRate);

  return {
    cui: row.cui,
    cui_name: str(row.cuiName),
    accession: row.accession,
    comparison_id: str(row.comparisonId, `${row.cui}__${row.accession}`),
    title: str(row.title),
    overall_design: str(row.overallDesign),
    year: num(row.year),
    platform: str(row.platform),
    n_samples: num(row.nsamples),
    n_case: num(row.ncase),
    n_control: num(row.ncontrol),
    usable_for_deg: Boolean(row.usableForDeg),
    has_deg: Boolean(row.hasDeg),
    n_genes_tested: num(row.ngenesTested),
    n_genes_sig: num(row.ngenesSig),
    qc_flags: qcFlags,
    summary: str(row.summary),
    pubmedids: str(row.pubmedids),
    control_caveat: str(row.controlCaveat),
    qc_flag_rate: qcFlagRate,
    has_gsea_curated: Boolean(row.hasGseaCurated),
    has_gsea_all: Boolean(row.hasGseaAll),
    has_gsea_reactome: Boolean(row.hasGseaReactome),
    has_gsea_wikipathways: Boolean(row.hasGseaWikipathways),
    covariates_in_model: str(row.covariatesInModel),
    confident_sex_mismatches: num(row.confidentSexMismatches),
    confident_group_consistency_flags: num(row.confidentGroupConsistencyFlags),
  };
}

export function mapApiSample(row: ApiSample): GeoBrowseSample {
  return {
    sample_id: row.sampleId,
    group: str(row.group),
    sex: str(row.sex),
    sex_source: str(row.sexSource, 'unknown'),
    ...(row.tissue ? { tissue: str(row.tissue) } : {}),
  };
}

export function mapApiPcaPoint(row: ApiPcaPoint): GeoBrowsePcaPoint {
  return {
    sample_id: row.sampleId,
    group: str(row.group),
    pc1: num(row.pc1),
    pc2: num(row.pc2),
    pct_var_pc1: num(row.pctVarPc1),
    pct_var_pc2: num(row.pctVarPc2),
  };
}

export function mapApiDegRow(row: ApiDegRow): GeoBrowseDegRow {
  return {
    gene_id: num(row.geneId),
    gene_symbol: row.geneSymbol,
    comparison_id: row.comparisonId,
    baseMean: num(row.baseMean),
    log2FoldChange: num(row.log2FoldChange),
    lfcSE: num(row.lfcSE),
    stat: num(row.stat),
    pvalue: num(row.pvalue),
    padj: num(row.padj),
  };
}

export function mapApiVolcanoPoint(
  row: ApiVolcanoPoint,
  thresholds?: { padj: number; fc: number },
): GeoBrowseVolcanoPoint {
  const log2FoldChange = num(row.log2FoldChange);
  const padj = num(row.padj);
  const significant =
    row.significant ??
    (thresholds ? padj < thresholds.padj && Math.abs(log2FoldChange) >= thresholds.fc : false);

  return {
    gene_id: num(row.geneId),
    gene_symbol: row.geneSymbol,
    log2FoldChange,
    padj,
    significant,
  };
}

export function mapApiEnrichmentRow(row: ApiEnrichmentRow): GeoBrowseEnrichmentRow {
  return {
    gene_set_id: row.geneSetId,
    gene_set_name: row.geneSetName,
    collection: row.collection,
    comparison_id: row.comparisonId,
    es: num(row.es),
    nes: num(row.nes),
    nom_pval: num(row.nomPval),
    fdr_qval: num(row.fdrQval),
    tag_pct: num(row.tagPct),
    gene_pct: num(row.genePct),
    lead_genes: str(row.leadGenes),
  };
}
